import { html, render, nothing, type TemplateResult } from 'lit-html';
import styles from './pk-path-tree.css?inline';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PkPathEntry {
    path: string;
    presencePct: number;
    distinctCount: number;
    sampleValues: unknown[];
}

export interface PkPromotedColumnEntry {
    name: string;
    presencePct: number;
    sampleValues: unknown[];
}

export interface PkPathTreeData {
    paths: PkPathEntry[];
    promotedColumns: PkPromotedColumnEntry[];
}

interface TreeNode {
    segment: string;
    /** Full dotted path to this node (for leaf selection and aria) */
    fullPath: string;
    children: Map<string, TreeNode>;
    /** Present only on leaf nodes */
    leaf?: PkPathEntry;
    expanded: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildTree(entries: PkPathEntry[]): TreeNode {
    const root: TreeNode = { segment: '', fullPath: '', children: new Map(), expanded: true };

    for (const entry of entries) {
        const segments = entry.path.split('.');
        let node = root;
        for (let i = 0; i < segments.length; i++) {
            const seg = segments[i] ?? '';
            if (!node.children.has(seg)) {
                const fullPath = segments.slice(0, i + 1).join('.');
                node.children.set(seg, {
                    segment: seg,
                    fullPath,
                    children: new Map(),
                    expanded: false,
                });
            }
            node = node.children.get(seg)!;
        }
        // Mark as leaf with data
        node.leaf = entry;
    }

    return root;
}

/** Check if a node or any of its descendants match the search query */
function nodeMatchesSearch(node: TreeNode, q: string): boolean {
    if (!q) return true;
    if (node.fullPath.toLowerCase().includes(q)) return true;
    for (const child of node.children.values()) {
        if (nodeMatchesSearch(child, q)) return true;
    }
    return false;
}

/**
 * `<pk-path-tree>` — collapsible JSON-path explorer for picking an extraction path.
 *
 * Set data via the `data` property (shape: `{ paths:[...], promotedColumns:[...] }`).
 *
 * Events:
 *   - `pk-path-select` — CustomEvent<{ path: string }>; bubbles + composed.
 *     `path` is the full dotted path prefixed with `$` (e.g. `$.resource.repository.name`)
 *     or the promoted-column name prefixed with `$promoted.` (e.g. `$promoted.Actor`).
 */
export class PkPathTree extends HTMLElement {
    static readonly tagName = 'pk-path-tree';

    private readonly _root: ShadowRoot;
    private _data: PkPathTreeData | null = null;
    private _tree: TreeNode | null = null;
    private _search = '';
    /** Roving focus: the currently focused item path */
    private _focusedPath = '';
    /** Bound listener refs so add/remove use the same function identity */
    private readonly _boundKeydown: EventListener;
    private readonly _boundClick: EventListener;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
        this._boundKeydown = (e: Event) => this._handleKeydown(e as KeyboardEvent);
        this._boundClick = (e: Event) => this._handleClick(e);
    }

    connectedCallback(): void {
        this._render();
        this._root.addEventListener('keydown', this._boundKeydown);
        this._root.addEventListener('click', this._boundClick);
    }

    disconnectedCallback(): void {
        this._root.removeEventListener('keydown', this._boundKeydown);
        this._root.removeEventListener('click', this._boundClick);
    }

    // ── Data API ──────────────────────────────────────────────────────────────

    get data(): PkPathTreeData | null {
        return this._data;
    }

    set data(val: PkPathTreeData | null) {
        this._data = val;
        this._tree = val ? buildTree(val.paths) : null;
        this._render();
    }

    /** Convenience: set paths directly */
    set paths(val: PkPathEntry[]) {
        this.data = {
            paths: val,
            promotedColumns: this._data?.promotedColumns ?? [],
        };
    }

    /** Convenience: set promotedColumns directly */
    set promotedColumns(val: PkPromotedColumnEntry[]) {
        this.data = {
            paths: this._data?.paths ?? [],
            promotedColumns: val,
        };
    }

    // ── Event handlers ────────────────────────────────────────────────────────

    private _handleClick(e: Event): void {
        const target = e.target as HTMLElement;

        // "Use" button on a leaf
        const useBtn = target.closest('[data-use-path]') as HTMLElement | null;
        if (useBtn) {
            const path = useBtn.dataset.usePath!;
            this._emitSelect(path);
            return;
        }

        // Branch toggle
        const toggle = target.closest('[data-toggle-path]') as HTMLElement | null;
        if (toggle) {
            this._toggleNode(toggle.dataset.togglePath!);
            return;
        }
    }

    private _handleKeydown(e: KeyboardEvent): void {
        const target = e.target as HTMLElement;
        const itemEl = target.closest('[data-item-path]') as HTMLElement | null;
        if (!itemEl) return;

        const path = itemEl.dataset.itemPath!;
        const isLeaf = itemEl.dataset.leaf === 'true';
        const isPromoted = itemEl.dataset.promoted === 'true';

        if (e.key === 'Enter') {
            e.preventDefault();
            if (isLeaf || isPromoted) this._emitSelect(this._eventPath(path, isPromoted));
            else this._toggleNode(path);
            return;
        }

        const focusable = this._getFocusableItems();
        const idx = focusable.findIndex(el => el.dataset.itemPath === path);

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                if (idx < focusable.length - 1) focusable[idx + 1]?.focus();
                break;
            case 'ArrowUp':
                e.preventDefault();
                if (idx > 0) focusable[idx - 1]?.focus();
                break;
            case 'ArrowRight':
                e.preventDefault();
                this._focusRight(path, isLeaf || isPromoted, focusable, idx);
                break;
            case 'ArrowLeft':
                e.preventDefault();
                this._focusLeft(path, isLeaf || isPromoted);
                break;
        }
    }

    /** ArrowRight: expand a collapsed branch, else step to the next focusable item. */
    private _focusRight(path: string, isLeafOrPromoted: boolean, focusable: HTMLElement[], idx: number): void {
        if (isLeafOrPromoted) return;
        const node = this._findNode(path);
        if (node && !node.expanded) {
            this._toggleNode(path);
        } else if (idx < focusable.length - 1) {
            focusable[idx + 1]?.focus();
        }
    }

    /** ArrowLeft: collapse an expanded branch, else move focus to the parent item. */
    private _focusLeft(path: string, isLeafOrPromoted: boolean): void {
        if (isLeafOrPromoted) return;
        const node = this._findNode(path);
        if (node && node.expanded) {
            this._toggleNode(path);
            return;
        }
        const parentPath = path.includes('.') ? path.slice(0, path.lastIndexOf('.')) : '';
        if (!parentPath) return;
        const parentEl = this._root.querySelector(`[data-item-path="${CSS.escape(parentPath)}"]`) as HTMLElement | null;
        parentEl?.focus();
    }

    private _getFocusableItems(): HTMLElement[] {
        return Array.from(this._root.querySelectorAll<HTMLElement>('[data-item-path]'));
    }

    private _findNode(path: string): TreeNode | null {
        if (!this._tree) return null;
        const segs = path.split('.');
        let node = this._tree;
        for (const seg of segs) {
            const child = node.children.get(seg);
            if (!child) return null;
            node = child;
        }
        return node;
    }

    private _toggleNode(path: string): void {
        const node = this._findNode(path);
        if (node) {
            node.expanded = !node.expanded;
            this._render();
            // Re-focus the same item after render
            requestAnimationFrame(() => {
                const all = this._root.querySelectorAll<HTMLElement>('[data-item-path]');
                const el = Array.from(all).find(e => e.dataset.itemPath === path) ?? null;
                el?.focus();
            });
        }
    }

    private _eventPath(nodePath: string, isPromoted: boolean): string {
        if (isPromoted) return `$promoted.${nodePath}`;
        return `$.${nodePath}`;
    }

    private _emitSelect(path: string): void {
        this.dispatchEvent(new CustomEvent<{ path: string }>('pk-path-select', {
            detail: { path },
            bubbles: true,
            composed: true,
        }));
    }

    // ── Rendering ─────────────────────────────────────────────────────────────

    private _template(): TemplateResult {
        const q = this._search.toLowerCase();

        return html`
            <style>${styles}</style>
            <div class="path-tree">
                <div class="path-tree__search">
                    <input
                        class="path-tree__search-input"
                        type="search"
                        placeholder="Filter paths…"
                        aria-label="Filter paths"
                        .value="${this._search}"
                        @input="${(e: Event) => {
                            this._search = (e.target as HTMLInputElement).value;
                            this._render();
                        }}"
                    />
                </div>

                ${!this._data ? html`<p class="path-tree__empty">No data loaded.</p>` : nothing}

                ${this._tree && this._tree.children.size > 0 ? html`
                    <ul class="path-tree__list" role="tree" aria-label="JSON paths">
                        ${this._renderChildren(this._tree, 0, q)}
                    </ul>
                ` : nothing}

                ${this._data && this._data.promotedColumns && this._data.promotedColumns.length > 0 ? html`
                    <div class="path-tree__section" role="group" aria-label="Promoted columns">
                        <div class="path-tree__section-title">Promoted columns</div>
                        <ul class="path-tree__list" role="tree" aria-label="Promoted columns">
                            ${this._data.promotedColumns
                                .filter(col => !q || col.name.toLowerCase().includes(q))
                                .map(col => this._renderPromotedColumn(col))}
                        </ul>
                    </div>
                ` : nothing}
            </div>
        `;
    }

    private _renderChildren(parentNode: TreeNode, depth: number, q: string): TemplateResult[] {
        const result: TemplateResult[] = [];

        for (const [, node] of parentNode.children) {
            if (!nodeMatchesSearch(node, q)) continue;

            const isLeaf = node.children.size === 0;
            const isBranch = !isLeaf;
            const isArray = node.segment === '[*]';

            if (isLeaf && node.leaf) {
                result.push(this._renderLeaf(node, depth, q));
            } else if (isBranch) {
                result.push(this._renderBranch(node, depth, q, isArray));
            }
        }

        return result;
    }

    private _renderBranch(node: TreeNode, depth: number, q: string, isArray: boolean): TemplateResult {
        const expanded = node.expanded || !!q; // auto-expand on search
        const indent = depth * 1.25;
        const segClass = isArray ? 'path-tree__segment path-tree__segment--array' : 'path-tree__segment';

        return html`
            <li
                role="treeitem"
                aria-expanded="${expanded ? 'true' : 'false'}"
                data-item-path="${node.fullPath}"
                data-toggle-path="${node.fullPath}"
                tabindex="${this._focusedPath === node.fullPath ? '0' : '-1'}"
                class="path-tree__item path-tree__item--branch"
                style="padding-left:${indent}rem"
            >
                <button
                    class="path-tree__toggle"
                    aria-label="${expanded ? 'Collapse' : 'Expand'} ${node.segment}"
                    data-toggle-path="${node.fullPath}"
                    tabindex="-1"
                >
                    <span class="path-tree__arrow ${expanded ? 'path-tree__arrow--open' : ''}">&#9658;</span>
                </button>
                <span class="${segClass}">${node.segment}</span>
                ${node.children.size > 0 ? html`
                    <span class="path-tree__child-count" aria-hidden="true">${node.children.size}</span>
                ` : nothing}
            </li>
            ${expanded ? html`
                <ul role="group" class="path-tree__group">
                    ${this._renderChildren(node, depth + 1, q)}
                </ul>
            ` : nothing}
        `;
    }

    private _renderLeaf(node: TreeNode, depth: number, _q: string): TemplateResult {
        const entry = node.leaf!;
        const indent = depth * 1.25;
        const samples = (entry.sampleValues ?? []).slice(0, 4);
        const presencePct = entry.presencePct ?? 0;
        const presenceColor = presencePct >= 75
            ? 'var(--pk-color-success, #2A7E4F)'
            : presencePct >= 40
            ? 'var(--pk-color-primary, #0E7C7B)'
            : 'var(--pk-color-warn, #C77E07)';
        const eventPath = `$.${node.fullPath}`;

        return html`
            <li
                role="treeitem"
                data-item-path="${node.fullPath}"
                data-leaf="true"
                tabindex="${this._focusedPath === node.fullPath ? '0' : '-1'}"
                class="path-tree__item path-tree__item--leaf"
                style="padding-left:${indent}rem"
                aria-label="${node.segment} — ${presencePct.toFixed(0)}% presence"
            >
                <span class="path-tree__segment path-tree__segment--leaf">${node.segment}</span>
                <span class="path-tree__presence" style="color:${presenceColor}" aria-label="${presencePct.toFixed(0)}% present">
                    ${presencePct.toFixed(0)}%
                </span>
                <span class="path-tree__distinct" title="Distinct values: ${entry.distinctCount}">
                    ${entry.distinctCount}
                </span>
                <span class="path-tree__samples" aria-hidden="true">
                    ${samples.map(v => html`<span class="path-tree__chip">${String(v)}</span>`)}
                </span>
                <button
                    class="path-tree__use-btn"
                    data-use-path="${eventPath}"
                    aria-label="Use path ${eventPath}"
                    tabindex="-1"
                >
                    Use
                </button>
            </li>
        `;
    }

    private _renderPromotedColumn(col: PkPromotedColumnEntry): TemplateResult {
        const samples = (col.sampleValues ?? []).slice(0, 4);
        const presencePct = col.presencePct ?? 0;
        const presenceColor = presencePct >= 75
            ? 'var(--pk-color-success, #2A7E4F)'
            : presencePct >= 40
            ? 'var(--pk-color-primary, #0E7C7B)'
            : 'var(--pk-color-warn, #C77E07)';
        const eventPath = `$promoted.${col.name}`;

        return html`
            <li
                role="treeitem"
                data-item-path="${col.name}"
                data-promoted="true"
                tabindex="-1"
                class="path-tree__item path-tree__item--leaf path-tree__item--promoted"
                aria-label="${col.name} — ${presencePct.toFixed(0)}% presence"
            >
                <span class="path-tree__segment path-tree__segment--leaf">${col.name}</span>
                <span class="path-tree__presence" style="color:${presenceColor}" aria-label="${presencePct.toFixed(0)}% present">
                    ${presencePct.toFixed(0)}%
                </span>
                <span class="path-tree__samples" aria-hidden="true">
                    ${samples.map(v => html`<span class="path-tree__chip">${String(v)}</span>`)}
                </span>
                <button
                    class="path-tree__use-btn"
                    data-use-path="${eventPath}"
                    aria-label="Use column ${col.name}"
                    tabindex="-1"
                >
                    Use
                </button>
            </li>
        `;
    }

    private _render(): void {
        render(this._template(), this._root);
    }
}

if (!customElements.get(PkPathTree.tagName)) {
    customElements.define(PkPathTree.tagName, PkPathTree);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-path-tree': PkPathTree;
    }
}
